import { useEffect, useState } from 'react'
import { MapPin } from 'lucide-react'
import { Link } from '../router.js'
import { apiGet } from '../admin/primitives.js'
import { useSoiree } from './SoireeData.js'
import { MarketEventCard, ageFr, type MarketEventLike } from './MarketEventCard.js'
import { MarketEventDrawer, type MarketEventDetail } from './MarketEventDrawer.js'

/**
 * AUTOUR DE RENNES — CETTE SOIRÉE.
 *
 * Widget du Page Layout Engine comme le reste de la page. La programmation,
 * elle, vit dans SALLES : une passe précédente en avait fait une section
 * séparée, ce qui donnait deux concepts parallèles pour le même métier.
 */

/* ═══════════ AUTOUR DE RENNES — CETTE SOIRÉE (§37-§40) ═══════════════ */

interface Signal {
  id: string; venue_name: string | null; type_label: string; summary: string
  impact: 'HIGH' | 'MEDIUM' | 'LOW'; event_start_at: string | null; last_seen_at: string
}
interface NightCtx {
  window: { from: string; to: string }
  events: MarketEventLike[]
  signals: Signal[]
  counts: { events: number; signals: number }
}

/**
 * CE QUI SE PASSE À RENNES PENDANT CETTE NUIT.
 *
 * Pas « le week-end à venir » : la nuit de CETTE soirée, et rien d'autre.
 * Une soirée du vendredi 23 h 30 se termine le samedi à 6 h — un concurrent
 * qui ouvre samedi 00 h 30 joue contre nous, pas le lendemain. Le
 * rattachement se fait donc par CHEVAUCHEMENT de fenêtre, jamais par date
 * calendaire.
 *
 * Ce bloc est un RÉSUMÉ opérationnel, pas une copie de la page de veille :
 * il montre le plus important et renvoie au reste.
 */
export function NightContextWidget() {
  const { id } = useSoiree()
  const [d, setD] = useState<NightCtx | null>(null)
  const [capture, setCapture] = useState<string | null>(null)
  // §25 — le même panneau que partout ailleurs. Une seule primitive.
  const [sel, setSel] = useState<MarketEventDetail | null>(null)

  useEffect(() => {
    if (!id) return
    void apiGet<NightCtx>(`/api/v1/soirees/${id}/night-context`).then(setD).catch(() => setD(null))
  }, [id])

  const rien = d !== null && d.counts.events === 0 && d.counts.signals === 0

  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Autour de Rennes — cette soirée">
      <header className="lc-panel__head">
        <h2 className="lc-section-title">
          <MapPin size={16} aria-hidden="true" /> Autour de Rennes — cette soirée
        </h2>
        {d && !rien ? (
          <span className="lc-hint">
            {[
              d.counts.events > 0
                ? `${d.counts.events} événement${d.counts.events > 1 ? 's' : ''} concurrent${d.counts.events > 1 ? 's' : ''}`
                : null,
              d.counts.signals > 0
                ? `${d.counts.signals} promotion${d.counts.signals > 1 ? 's' : ''} remarquable${d.counts.signals > 1 ? 's' : ''}`
                : null,
            ].filter(Boolean).join(' · ')}
          </span>
        ) : null}
      </header>
      <div className="lc-panel__body sp-night">
        {!d ? <p className="lc-hint">Chargement…</p>
          : rien ? (
            <p className="gd-calm">
              Rien de notable détecté chez les concurrents pour cette nuit.
              Un silence est une information, tant que les sources sont saines.
            </p>
          ) : (
            <>
              {d.events.length > 0 ? (
                <div className="mev-grid mev-grid--compact">
                  {d.events.map((e) => (
                    <MarketEventCard key={e.id} e={e} densite="compact"
                      onOpen={() => setSel(e as MarketEventDetail)} onCapture={setCapture} />
                  ))}
                </div>
              ) : null}

              {d.signals.length > 0 ? (
                <ul className="sp-night__sigs">
                  {d.signals.map((s) => (
                    <li key={s.id} className={`sp-night__sig sp-night__sig--${s.impact.toLowerCase()}`}>
                      <span className="sp-night__sig__venue">
                        {s.venue_name ?? 'Établissement à identifier'}
                      </span>
                      <span className="sp-night__sig__txt">{s.summary}</span>
                      <em className="lc-hint">{ageFr(s.last_seen_at)}</em>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}

        <p className="lc-hint sp-night__all">
          <Link href="/soirees/market">Voir toute la veille</Link>
        </p>
      </div>

      <MarketEventDrawer event={sel} onClose={() => setSel(null)} />

      {capture ? (
        <div className="so-capture" role="dialog" aria-label="Affiche analysée"
          onClick={() => setCapture(null)}>
          <img src={`/api/v1/market/social/stories/${capture}/capture`} alt="Affiche analysée" />
        </div>
      ) : null}
    </section>
  )
}
